import React, { useEffect, useRef, useState } from "react";
import "./GraphView.css";
import * as d3 from "d3";
import {
  LinksResponse,
  ProcessedEdge,
  LinkNode,
} from "../../types/visualization";
import { PieceDisplayMode } from "../../types/chess";
import VisualizationContainer from "./VisualizationContainer";
import {
  squareToCoords,
  gridToScreen,
  screenToSquare,
} from "../../utils/graphCoordinates";
import { getEdgeStyle, arrowheadColors } from "../../utils/graphStyles";
import { renderGrid, renderCoordinates, renderHeatmap } from "./GraphGrid";
import { renderNodes, renderPhantomMarkers } from "./GraphNodes";

interface GraphViewProps {
  linksData: LinksResponse | null;
  processedEdges: ProcessedEdge[];
  displayMode: PieceDisplayMode;
  showGrid?: boolean;
  flipBoard?: boolean;
  heatmap?: boolean;
  lastMoveUCI?: string;
  onMoveAttempt?: (
    fromSquare: string,
    toSquare: string,
    uciMove: string
  ) => boolean;
}

const GraphView: React.FC<GraphViewProps> = ({
  linksData,
  processedEdges,
  displayMode,
  showGrid = false,
  flipBoard = false,
  heatmap = false,
  lastMoveUCI,
  onMoveAttempt,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 600, height: 600 });

  // Track pending moves to avoid re-rendering with stale data
  const pendingMoveRef = useRef<{
    from: string;
    to: string;
    pieceType: string;
  } | null>(null);

  // Handle container resize
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setDimensions({
          width: Math.max(600, width),
          height: Math.max(600, height),
        });
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!linksData || !processedEdges || !svgRef.current) return;

    // Check if there's a pending move that hasn't been reflected in the data yet
    if (pendingMoveRef.current) {
      const { from, to, pieceType } = pendingMoveRef.current;
      const pieceAtFrom = linksData.nodes?.find(
        (n: LinkNode) => n.square === from && n.piece_type !== "phantom"
      );
      const pieceAtTo = linksData.nodes?.find(
        (n: LinkNode) => n.square === to && n.piece_type !== "phantom"
      );
      console.log("[EFFECT:pending]", {
        from,
        to,
        pieceType,
        pieceAtFrom: pieceAtFrom?.piece_type,
        pieceAtTo: pieceAtTo?.piece_type,
      });

      const pieceStillAtFrom = pieceAtFrom?.piece_type === pieceType;
      const movedPieceAtTo = pieceAtTo?.piece_type === pieceType;

      if (pieceStillAtFrom || !movedPieceAtTo) {
        // Data is stale - skip this render to avoid snap-back
        console.log("[EFFECT:pending] SKIPPING - stale data");
        return;
      } else {
        // Data has been updated - clear the pending move
        console.log("[EFFECT:pending] data updated, clearing");
        pendingMoveRef.current = null;
      }
    }

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const { width, height } = dimensions;

    svg
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("preserveAspectRatio", "xMidYMid meet");

    const g = svg.append("g");

    if (showGrid) {
      if (heatmap) {
        renderHeatmap(g, linksData.nodes, width, height, flipBoard);
      } else {
        renderGrid(g, width, height);
      }
      renderCoordinates(g, width, height, flipBoard);
    }

    type SimulationNode = LinkNode & d3.SimulationNodeDatum;
    type SimulationLink = {
      source: SimulationNode;
      target: SimulationNode;
      type: string;
    };

    const nodes = linksData.nodes.map((node: LinkNode) => {
      const gridCoords = squareToCoords(node.square);
      let [x, y] = gridToScreen(gridCoords, width, height);

      if (flipBoard) {
        x = width - x;
        y = height - y;
      }

      return {
        ...node,
        x,
        y,
        fx: x,
        fy: y,
      };
    }) as SimulationNode[];

    const validEdges = processedEdges.filter((edge) => {
      const sourceNode = nodes.find((n) => n.square === edge.source);
      const targetNode = nodes.find((n) => n.square === edge.target);
      return sourceNode && targetNode;
    });

    const links = validEdges.map((edge) => ({
      source: nodes.find((n) => n.square === edge.source)!,
      target: nodes.find((n) => n.square === edge.target)!,
      type: edge.type,
    })) as SimulationLink[];

    const simulation = d3
      .forceSimulation<SimulationNode>(nodes)
      .force(
        "link",
        d3
          .forceLink<SimulationNode, SimulationLink>(links)
          .id((d) => d.square)
          .distance(50)
          .strength(0)
      )
      .force("charge", d3.forceManyBody().strength(0))
      .force(
        "collision",
        d3
          .forceCollide()
          .radius(25)
          .strength(showGrid ? 0 : 1)
      );

    if (showGrid) {
      simulation.stop();
    }

    svg
      .append("defs")
      .selectAll("marker")
      .data([
        "arrowheadThreat",
        "arrowheadProtection",
        "arrowheadCasterThreat",
        "arrowheadCasterProtection",
        "arrowheadShadowThreat",
        "arrowheadShadowProtection",
        "arrowheadAdjacency",
        "arrowheadGray",
        "arrowheadLastMove",
      ])
      .join("marker")
      .attr("id", (d) => d)
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", 20)
      .attr("refY", 0)
      .attr("markerWidth", 6)
      .attr("markerHeight", 6)
      .attr("orient", "auto")
      .append("path")
      .attr("fill", (d) => arrowheadColors[d] || arrowheadColors.arrowheadGray)
      .attr("d", "M0,-5L10,0L0,5");

    // For opacity mode, define per-edge linear gradients
    // Define per-edge linear gradients that fade from transparent at source to opaque at target
    const defs = svg.select("defs");
    links.forEach((d, i) => {
      const style = getEdgeStyle(d.type);
      const grad = defs
        .append("linearGradient")
        .attr("id", `edgeGrad-${i}`)
        .attr("gradientUnits", "userSpaceOnUse")
        .attr("x1", d.source.x!)
        .attr("y1", d.source.y!)
        .attr("x2", d.target.x!)
        .attr("y2", d.target.y!);
      grad
        .append("stop")
        .attr("offset", "0%")
        .attr("stop-color", style.color)
        .attr("stop-opacity", 0.1);
      grad
        .append("stop")
        .attr("offset", "100%")
        .attr("stop-color", style.color)
        .attr("stop-opacity", 1);
    });

    const linkGroup = g.append("g");

    const link = linkGroup
      .selectAll<SVGLineElement, SimulationLink>("line")
      .data(links)
      .join("line")
      .attr("stroke", (_d, i) => `url(#edgeGrad-${i})`)
      .attr("stroke-width", (d) => getEdgeStyle(d.type).width)
      .attr("marker-end", (d) => getEdgeStyle(d.type).marker);

    const updateLinkPositions = () => {
      link
        .attr("x1", (d) => d.source.x!)
        .attr("y1", (d) => d.source.y!)
        .attr("x2", (d) => d.target.x!)
        .attr("y2", (d) => d.target.y!);
      links.forEach((d, i) => {
        defs
          .select(`#edgeGrad-${i}`)
          .attr("x1", d.source.x!)
          .attr("y1", d.source.y!)
          .attr("x2", d.target.x!)
          .attr("y2", d.target.y!);
      });
    };

    // Draw yellow line for the last move with directional width variation
    if (lastMoveUCI && lastMoveUCI.length >= 4) {
      const fromSquare = lastMoveUCI.slice(0, 2);
      const toSquare = lastMoveUCI.slice(2, 4);

      // Calculate coordinates directly from square names (don't rely on nodes existing)
      const fromGridCoords = squareToCoords(fromSquare);
      const toGridCoords = squareToCoords(toSquare);
      let [fromX, fromY] = gridToScreen(fromGridCoords, width, height);
      let [toX, toY] = gridToScreen(toGridCoords, width, height);

      if (flipBoard) {
        fromX = width - fromX;
        fromY = height - fromY;
        toX = width - toX;
        toY = height - toY;
      }

      // Create a tapered path that goes from thin to thick
      const dx = toX - fromX;
      const dy = toY - fromY;
      const length = Math.sqrt(dx * dx + dy * dy);

      // Perpendicular vector for width
      const perpX = -dy / length;
      const perpY = dx / length;

      // Width at start and end (matching other edges)
      const startWidth = 0.2;
      const endWidth = 2;

      // Create a polygon path that tapers
      const pathData = [
        `M ${fromX + perpX * startWidth} ${fromY + perpY * startWidth}`,
        `L ${toX + perpX * endWidth} ${toY + perpY * endWidth}`,
        `L ${toX - perpX * endWidth} ${toY - perpY * endWidth}`,
        `L ${fromX - perpX * startWidth} ${fromY - perpY * startWidth}`,
        'Z'
      ].join(' ');

      g.append("path")
        .attr("class", "last-move-arrow")
        .attr("d", pathData)
        .attr("fill", "gold")
        .attr("fill-opacity", 0.8);
    }

    const visibleNodes = nodes.filter((d) => d.piece_type && d.piece_type !== "phantom");
    const phantomNodes = nodes.filter((d) => d.piece_type === "phantom");

    const dragBehavior = d3
      .drag<SVGGElement, SimulationNode>()
      .on("start", (event, d) => {
        if (showGrid && onMoveAttempt) {
          (d as any).startSquare = d.square;
          d.fx = d.x;
          d.fy = d.y;
        } else {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        }
      })
      .on("drag", function (event, d) {
        d.fx = event.x;
        d.fy = event.y;
        d.x = event.x;
        d.y = event.y;

        if (showGrid && onMoveAttempt) {
          d3.select(this).attr("transform", `translate(${event.x},${event.y})`);
        }
      })
      .on("end", (event, d) => {
        if (showGrid && onMoveAttempt) {
          let targetX = event.x;
          let targetY = event.y;

          if (flipBoard) {
            targetX = width - targetX;
            targetY = height - targetY;
          }

          const targetSquare = screenToSquare(targetX, targetY, width, height);
          const startSquare = (d as any).startSquare;
          let moveWasValid = false;

          if (targetSquare && startSquare && targetSquare !== startSquare) {
            const uciMove = startSquare + targetSquare;
            // Set pending move before calling onMoveAttempt to catch stale re-renders
            pendingMoveRef.current = {
              from: startSquare,
              to: targetSquare,
              pieceType: d.piece_type,
            };
            moveWasValid = onMoveAttempt(startSquare, targetSquare, uciMove);
            if (!moveWasValid) {
              // Move was rejected, clear the pending move
              pendingMoveRef.current = null;
            }
          }

          if (!moveWasValid) {
            const gridCoords = squareToCoords(d.square);
            let [originalX, originalY] = gridToScreen(
              gridCoords,
              width,
              height
            );

            if (flipBoard) {
              originalX = width - originalX;
              originalY = height - originalY;
            }

            d.fx = originalX;
            d.fy = originalY;
          } else {
            delete d.fx;
            delete d.fy;
          }

          delete (d as any).startSquare;
        } else {
          if (!event.active) simulation.alphaTarget(0);
          delete d.fx;
          delete d.fy;
        }
      });

    const node = renderNodes(
      g,
      visibleNodes,
      links,
      displayMode,
      showGrid,
      height,
      dragBehavior
    );
    const phantomMarkers = renderPhantomMarkers(g, phantomNodes, links);

    if (showGrid) {
      nodes.forEach((node) => {
        const gridCoords = squareToCoords(node.square);
        let [x, y] = gridToScreen(gridCoords, width, height);

        if (flipBoard) {
          x = width - x;
          y = height - y;
        }

        node.x = x;
        node.y = y;
        node.fx = x;
        node.fy = y;
      });

      node.attr("transform", (d) => `translate(${d.x},${d.y})`);
      phantomMarkers.attr("x", (d) => d.x!).attr("y", (d) => d.y!);

      updateLinkPositions();
    }

    simulation.on("tick", () => {
      updateLinkPositions();

      node.attr("transform", (d) => `translate(${d.x},${d.y})`);

      phantomMarkers.attr("x", (d) => d.x!).attr("y", (d) => d.y!);
    });
  }, [
    linksData,
    processedEdges,
    displayMode,
    showGrid,
    flipBoard,
    heatmap,
    lastMoveUCI,
    onMoveAttempt,
    dimensions,
  ]);

  return (
    <VisualizationContainer className="graph-view-container" ref={containerRef}>
      <svg ref={svgRef} className="visualization-svg graph-view" />
    </VisualizationContainer>
  );
};

export default GraphView;
